import React from 'react';
import {
  HStack, Icon, Pressable, Spinner, Text,
} from '@gluestack-ui/themed';
import { WandSparkles } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

interface GenerateWithAIBadgeProps {
  /** Called when the badge is pressed */
  onGenerate: () => void;
  /** Shows a spinner and disables interaction while streaming */
  isStreaming?: boolean;
  /** Renders the badge in a dimmed, non-interactive state when false */
  enabled?: boolean;
  /** Override the default "GENERATE WITH AI" label */
  label?: string;
  /** testID for automated tests */
  testID?: string;
}

/**
 * GenerateWithAIBadge
 * ---
 * Pill-shaped badge that triggers AI generation. Shows a spinner during streaming.
 * Used as a right-aligned action trigger in form sections (description, objectives, etc.).
 */
const GenerateWithAIBadge: React.FC<GenerateWithAIBadgeProps> = ({
  onGenerate,
  isStreaming = false,
  enabled = true,
  label,
  testID,
}) => {
  const { t } = useTranslation();

  if (isStreaming) {
    return (
      <HStack alignItems="center" space="xs">
        <Spinner size={10} mr="$1" />
        <Text size="2xs" color="$textSubtle">{t('generateWithAI.generating')}</Text>
      </HStack>
    );
  }

  return (
    <Pressable
      testID={testID}
      onPress={enabled ? onGenerate : undefined}
      flexDirection="row"
      alignItems="center"
      px="$2"
      py="$1"
      borderRadius="$sm"
      bg="$bgSubtle"
      opacity={enabled ? 1 : 0.4}
      $hover={{ bg: enabled ? '$bgHover' : '$bgSubtle' }}
    >
      <Icon as={WandSparkles} size="xs" color="$textSubtle" mr="$1" />
      <Text size="2xs" color="$textSubtle" fontWeight="bold">
        {label ?? t('generateWithAI.label')}
      </Text>
    </Pressable>
  );
};

export default GenerateWithAIBadge;
